import { useState, useEffect, useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { content } from '../data/content'
import { lazy, Suspense } from 'react'
import SEO from '../components/SEO'
import { useLanguage } from '../context/LanguageContext'

// Lazy Load components below the fold for instant Hero rendering
const StatsGrid = lazy(() => import('../components/StatsGrid'));
const ValueProps = lazy(() => import('../components/ValueProps'));
const ServiceHighlights = lazy(() => import('../components/ServiceHighlights'));
const CaseStudies = lazy(() => import('../components/CaseStudies'));
const Ecosystem = lazy(() => import('../components/Ecosystem'));
const MediaModal = lazy(() => import('../components/MediaModal'));
const ContactForm = lazy(() => import('../components/ContactForm'));
const LatestInsights = lazy(() => import('../components/LatestInsights'));
const GlobalReachMap = lazy(() => import('../components/GlobalReachMap'));


// A wrapper that delays rendering until the element is near the viewport
function DeferredRender({ children, fallback }) {
    const [shouldRender, setShouldRender] = useState(false);
    const containerRef = useRef(null);
    
    useEffect(() => {
        if (!containerRef.current) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setShouldRender(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '300px' } // Pre-load slightly before scrolling into view
        );
        
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    if (shouldRender) return children;
    
    return <div ref={containerRef} style={{ width: '100%' }}>{fallback}</div>;
}

function Home() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const location = useLocation();
    const { lang } = useLanguage();

    useEffect(() => {
        if (location.state?.scrollTo === 'contact') {
            const element = document.getElementById('contact');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [location]);

    const activeContent = content[lang] || content.en;
    const headline = activeContent.hero.headline;
    const headlineLines = headline.split('\n');

    return (
        <>
            <SEO
                title={lang === 'tr' ? "Go Nova Medya | YouTube Content ID ile Gelirinizi Maksimize Edin" : "Go Nova Media | YouTube Content ID & AVOD Revenue Optimization"}
                description={activeContent.hero.subtitle.split('\n\n')[0]}
                ogTitle={headline.replace('\n', ' ')}
            />
            {/* Hero Section — Static, LCP-optimised */}
            <section className="hero-standard hero">
                <div className="container" style={{ position: 'relative' }}>

                    {/* Single Static Headline — matches skeleton exactly for perfect LCP */}
                    <div className="hero-headline-container">
                        <h1 className={`text-gradient hero-title ${lang === 'tr' ? 'tr' : ''}`}>
                            {headlineLines.reduce((prev, curr, i) => [prev, <br key={i} />, curr])}
                        </h1>
                    </div>

                    <div className="hero-subtext" style={{ marginBottom: '35px' }}>
                        <p style={{ marginBottom: '12px', fontWeight: 400 }}>
                            {activeContent.hero.subtitle.split('\n\n')[0]}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <p style={{ fontWeight: 700, color: 'white' }}>
                                {activeContent.hero.subtitle.split('\n\n')[1]?.split('\n')[0]}
                            </p>
                            <p style={{ fontWeight: 400, opacity: 0.8, fontSize: '0.9em' }}>
                                {activeContent.hero.subtitle.split('\n\n')[1]?.split('\n')[1]}
                            </p>
                        </div>
                    </div>

                    <div className="hero-cta-wrapper">
                        <div className="cta-group hero-cta" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="glass-panel cta-glow-effect"
                                style={{
                                    padding: '16px 48px',
                                    borderRadius: '50px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    fontSize: '1.25rem',
                                    fontWeight: 800,
                                    background: 'white',
                                    color: 'black',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                                }}
                            >
                                {lang === 'tr' ? 'Gelirinizi Açığa Çıkarın' : 'Unlock Your Revenue'} <ArrowRight size={24} />
                            </button>
                            <div className="trusted-text" style={{
                                marginTop: '9px',
                                fontSize: '9px',
                                fontWeight: 400,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                color: 'rgba(255, 255, 255, 0.6)'
                            }}>
                                {lang === 'tr' ? 'Lider global içerik sahipleri ve medya şirketleri tarafından güvenilir' : 'Trusted by leading global content owners and media companies'}
                            </div>
                        </div>
                    </div>

                    <Suspense fallback={null}>
                        <MediaModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
                    </Suspense>
                </div>
            </section>

            {/* Below-the-fold sections — lazy loaded & deferred */}
            <DeferredRender fallback={<div style={{ minHeight: '400px' }} />}>
                <Suspense fallback={<div style={{ minHeight: '400px' }} />}>
                    <StatsGrid />
                </Suspense>
            </DeferredRender>
            <DeferredRender fallback={<div style={{ minHeight: '600px' }} />}>
                <Suspense fallback={<div style={{ minHeight: '600px' }} />}>
                    <GlobalReachMap />
                </Suspense>
            </DeferredRender>

            <DeferredRender fallback={<div style={{ minHeight: '300px' }} />}>
                <Suspense fallback={<div style={{ minHeight: '300px' }} />}>
                    <LatestInsights />
                </Suspense>
            </DeferredRender>
            <DeferredRender fallback={<div style={{ minHeight: '600px' }} />}>
                <Suspense fallback={<div style={{ minHeight: '600px' }} />}>
                    <ServiceHighlights />
                </Suspense>
            </DeferredRender>
            <DeferredRender fallback={<div style={{ minHeight: '400px' }} />}>
                <Suspense fallback={<div style={{ minHeight: '400px' }} />}>
                    <ValueProps />
                </Suspense>
            </DeferredRender>
            <DeferredRender fallback={<div style={{ minHeight: '600px' }} />}>
                <Suspense fallback={<div style={{ minHeight: '600px' }} />}>
                    <CaseStudies />
                </Suspense>
            </DeferredRender>
            <DeferredRender fallback={<div style={{ minHeight: '700px' }} />}>
                <Suspense fallback={<div style={{ minHeight: '700px' }} />}>
                    <Ecosystem />
                </Suspense>
            </DeferredRender>
            <DeferredRender fallback={<div style={{ minHeight: '500px' }} />}>
                <Suspense fallback={<div style={{ minHeight: '500px' }} />}>
                    <ContactForm />
                </Suspense>
            </DeferredRender>
        </>
    );
}

export default Home;

